import { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import axios from 'axios';
import { Ingredient, FormulationBatchRow, User } from '../types/Types';
import { useToast } from '../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';

const STALE_VERSION_KEY = 'errors.ingredients.staleVersion';

/** 409 from the server: the normativ changed after this screen loaded it. */
export const isStaleVersionError = (err: any): boolean =>
  err?.response?.status === 409 && err?.response?.data?.messageKey === STALE_VERSION_KEY;

interface UseOSDetailOptions {
  archiveView?: boolean;
  /** Reloads the normativ into the editor — offered as „Освежи" on a version conflict. */
  onStaleVersion?: () => void;
}

export const useOSDetail = ({ archiveView = false, onStaleVersion }: UseOSDetailOptions = {}) => {
  const { showToast } = useToast();
  const { t } = useTranslation();

  // Current ingredient state
  const [ingredient, setIngredientState] = useState<Ingredient | null>(null);
  // Synchronous mirror: the version sent as expectedVersion must be the one the editor shows,
  // even when two writes run back-to-back before React re-renders.
  const ingredientRef = useRef<Ingredient | null>(null);
  const setIngredient = useCallback((next: Ingredient | null) => {
    ingredientRef.current = next;
    setIngredientState(next);
  }, []);
  const onStaleVersionRef = useRef(onStaleVersion);
  onStaleVersionRef.current = onStaleVersion;

  // The version the user is looking at — undefined when the screen shows another normativ
  // (then the server falls back to its own concurrency check, as for older clients).
  const expectedVersionFor = useCallback((id: string): number | undefined => {
    const current = ingredientRef.current;
    return current?._id === id && typeof current.__v === 'number' ? current.__v : undefined;
  }, []);

  // Toast with an „Освежи" action. Returns true when the error was a version conflict.
  const notifyIfStale = useCallback((err: any): boolean => {
    if (!isStaleVersionError(err)) return false;
    const reload = onStaleVersionRef.current;
    showToast(
      t(STALE_VERSION_KEY),
      'warning',
      10000,
      reload ? { label: t('errors.ingredients.staleVersionReload'), onClick: reload } : undefined
    );
    return true;
  }, [showToast, t]);
  const [loadingIngredient, setLoadingIngredient] = useState<boolean>(false);

  // List of ingredients for search/pagination
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loadingList, setLoadingList] = useState<boolean>(false);
  const [errorList, setErrorList] = useState<string | null>(null);

  // Search and pagination
  const [search, setSearchState] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 300);
  const [page, setPageState] = useState<number>(1);
  // BUG 3 fix (2nd pass): a ref mirror of `page`, written synchronously.
  // React flushes passive effects using the pre-re-render closure, so when the
  // debounced-search `useLayoutEffect` below calls `setPage(1)` in the same
  // commit as the fetch effect, the fetch effect's `page` closure variable
  // still reads the OLD value (e.g. 3) — firing an extra fetch for a stale
  // page. Reading `pageRef.current` in the fetch effect instead makes that
  // reset visible immediately, in the same commit, so only ONE fetch fires.
  const pageRef = useRef(1);
  const setPage = useCallback((p: number) => {
    pageRef.current = p;
    setPageState(p);
  }, []);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [absoluteTotal, setAbsoluteTotal] = useState<number>(0);
  const [reloadFlag, setReloadFlag] = useState(0);
  const requestId = useRef(0);
  const lastFetchKeyRef = useRef<string | null>(null);
  const initialLatestLoadStartedRef = useRef(false);
  // BUG 4 fix: counts requests currently in flight so `loadingList` can never
  // get stuck true — it is only cleared once every in-flight request has
  // settled, regardless of which one the `requestId` guard treats as "latest".
  const inFlightCount = useRef(0);

  // BUG 3 fix: the page reset used to happen here AND in the debounced-search
  // layout effect below. That let a page change fire the fetch effect while
  // `debouncedSearch` was still the OLD query, producing a wasted request for
  // stale text. Now there is exactly ONE page reset, tied to the debounced
  // value (see the `useLayoutEffect` below) — this only clears the visible
  // list immediately so the modal doesn't show stale rows while typing.
  const setSearch = useCallback((value: string) => {
    requestId.current += 1;
    setIngredients([]);
    setLoadingList(false);
    setErrorList(null);
    setSearchState(value);
  }, []);

  const apiUrl = process.env.REACT_APP_API_URL ?? '';
  const baseUrl = `${apiUrl}/api/ingredients`;

  // Users list for prepared/approved dropdowns
  const [usersList, setUsersList] = useState<User[]>([]);
  useEffect(() => {
    const token = localStorage.getItem('token') ?? '';
    axios
      .get<User[]>(`${apiUrl}/api/users`, { headers: { 'x-auth-token': token } })
      .then((res) => setUsersList(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [apiUrl]);

  const signIngredient = useCallback(
    async (id: string, field: 'prepared' | 'approved', pin: string): Promise<Ingredient | null> => {
      const token = localStorage.getItem('token') ?? '';
      try {
        const res = await axios.post(
          `${baseUrl}/${id}/sign/${field}`,
          { pin, expectedVersion: expectedVersionFor(id) },
          { headers: { 'x-auth-token': token } }
        );
        const signed = res.data as Ingredient;
        setIngredient(signed);
        return signed;
      } catch (err) {
        notifyIfStale(err);
        throw err;
      }
    },
    [baseUrl, expectedVersionFor, notifyIfStale, setIngredient]
  );

  const removeIngredientSignature = useCallback(
    async (id: string, field: 'prepared' | 'approved', pin: string, reason: string): Promise<Ingredient | null> => {
      const token = localStorage.getItem('token') ?? '';
      try {
        const res = await axios.delete(`${baseUrl}/${id}/sign/${field}`, {
          headers: { 'x-auth-token': token },
          data: { pin, reason, expectedVersion: expectedVersionFor(id) },
        });
        const updated = res.data as Ingredient;
        setIngredient(updated);
        return updated;
      } catch (err) {
        notifyIfStale(err);
        throw err;
      }
    },
    [baseUrl, expectedVersionFor, notifyIfStale, setIngredient]
  );

  // Fetch latest ingredient (like DocumentsMaterial pattern)
  const fetchLatestIngredient = useCallback(async () => {
    setLoadingIngredient(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.get<{
        ingredients: Ingredient[];
        totalCount: number;
      }>(baseUrl, {
        headers: { 'x-auth-token': token },
        params: {
          sort: 'ident',
          order: 'asc',
          page: 1,
          limit: 1,
          // /os/archive must open the first ARCHIVED normativ, not the first live one.
          ...(archiveView ? { tab: 'archive' } : {}),
        },
      });

      if (response.data.ingredients.length > 0) {
        setIngredient(response.data.ingredients[0]);
      } else {
        setIngredient(null);
      }
    } catch (err: any) {
      setIngredient(null);
    } finally {
      setLoadingIngredient(false);
    }
  }, [baseUrl, archiveView, setIngredient]);

  // Fetch ingredients list for search/dropdown (with accumulation like Partners)
  const fetchIngredientsList = useCallback(async (pageToFetch: number, resetList = false) => {
    const reqId = ++requestId.current;
    inFlightCount.current += 1;
    setLoadingList(true);
    setErrorList(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.get<{
        ingredients: Ingredient[];
        totalCount: number;
        page: number;
        totalPages: number;
      }>(baseUrl, {
        headers: { 'x-auth-token': token },
        params: {
          view: 'list',
          search: debouncedSearch,
          tab: archiveView ? 'archive' : 'active',
          sort: 'ident',
          order: 'asc',
          page: pageToFetch,
          limit: 10,
        },
      });

      if (reqId !== requestId.current) return;

      if (resetList || pageToFetch === 1) {
        setIngredients(response.data.ingredients);
      } else {
        // BUG 3 fix: never append onto an empty list — if the list was reset
        // (e.g. by a query change) while this request was in flight, treat it
        // as a fresh page 1 instead of stitching a middle page onto nothing.
        // Read via the state updater so `ingredients` doesn't need to be a dep.
        setIngredients(prev => (prev.length === 0 ? response.data.ingredients : [...prev, ...response.data.ingredients]));
      }
      setTotal(response.data.totalCount);
      setAbsoluteTotal(response.data.totalCount);
      setTotalPages(response.data.totalPages);
    } catch (err: any) {
      if (reqId !== requestId.current) return;
      const message = err.response?.data?.message || err.message || 'Failed to fetch ingredients';
      setErrorList(message);
      if (resetList || pageToFetch === 1) setIngredients([]);
    } finally {
      // BUG 4 fix: clear loading only once every in-flight request has
      // settled, not just the one the requestId guard calls "current" — a
      // superseded request must not leave `loadingList` stuck true.
      inFlightCount.current = Math.max(0, inFlightCount.current - 1);
      if (inFlightCount.current === 0) setLoadingList(false);
    }
  }, [baseUrl, debouncedSearch, archiveView]);

  // Load a specific ingredient by ID.
  // MUST stay referentially stable (useCallback): OS.tsx drives its "rebind editor to the
  // selected ident" effect off this identity. As a plain function it changed every render,
  // so that effect re-fired on every render — and while the selected ident has no normativ
  // yet it issued a GET /by-item/:id (404) per keystroke.
  const loadIngredientById = useCallback(async (id: string) => {
    setLoadingIngredient(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.get<Ingredient>(`${baseUrl}/${id}`, {
        headers: { 'x-auth-token': token },
      });

      setIngredient(response.data);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to load ingredient';
      showToast(message, 'error');
    } finally {
      setLoadingIngredient(false);
    }
  }, [baseUrl, showToast, setIngredient]);

  /**
   * Silent re-read of the shown normativ (keep-alive tab became visible again). Replaces it only
   * when the stored version moved on AND `canApply()` still allows it (no unsaved edits); returns
   * true then, so the screen can re-sync its form.
   * No spinner, no toast — a failed background check simply keeps what is on screen.
   */
  const refreshIngredientIfChanged = useCallback(async (
    id: string,
    canApply: () => boolean = () => true
  ): Promise<boolean> => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
      const response = await axios.get<Ingredient>(`${baseUrl}/${id}`, {
        headers: { 'x-auth-token': token },
      });
      const current = ingredientRef.current;
      if (current?._id !== id || response.data?.__v === current.__v || !canApply()) return false;
      setIngredient(response.data);
      return true;
    } catch {
      return false;
    }
  }, [baseUrl, setIngredient]);

  /**
   * The live normativ for an ident, or null when that ident has none yet.
   *
   * Lives here rather than inline in the screen (API calls belong in hooks). A 404 is the
   * NORMAL answer for an ident with no normativ, not an error — it is mapped to null and
   * never toasted, because the screen uses it to decide "edit existing vs create new".
   */
  const fetchIngredientByItem = useCallback(async (itemId: string): Promise<Ingredient | null> => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const res = await axios.get<{ ingredient: Ingredient }>(`${baseUrl}/by-item/${itemId}`, {
        headers: { 'x-auth-token': token },
      });
      return res.data?.ingredient ?? null;
    } catch {
      return null;
    }
  }, [baseUrl]);

  // Create a new ingredient
  const createIngredient = async (data: {
    items: string;
    batch?: number;
    batchUnit?: string | null;
    formulationBatch?: FormulationBatchRow[];
    waste?: number;
    plannedWaste?: number;
    description?: string;
    productionCode?: string;
    validFrom?: string;
    validTo?: string;
    accountingType?: string;
    workOrderDocument?: string;
    status?: string[];
  }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.post(baseUrl, data, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json',
        },
      });

      setIngredient(response.data.ingredient || response.data);
      // `sidebar.osBasicSpecification` = „Норматив" — this hook backs /os, NOT the older
      // „Составници" screen that `sidebar.basicSpecification` names.
      showToast(t('toast.created', { item: t('sidebar.osBasicSpecification') }), 'success');
      await fetchIngredientsList(1, true);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to create ingredient';
      const messageKey = err.response?.data?.messageKey;
      const translatedMessage = messageKey ? t(messageKey) : message;
      showToast(translatedMessage, 'error');
      throw err;
    }
  };

  // Update an existing ingredient
  const updateIngredient = async (id: string, data: {
    items?: string;
    batch?: number;
    batchUnit?: string | null;
    formulationBatch?: FormulationBatchRow[];
    waste?: number;
    plannedWaste?: number;
    description?: string;
    validFrom?: string;
    validTo?: string;
    accountingType?: string;
    workOrderDocument?: string;
    status?: string[];
  }, options?: { quiet?: boolean }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.put(`${baseUrl}/${id}`, { ...data, expectedVersion: expectedVersionFor(id) }, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json',
        },
      });

      setIngredient(response.data.ingredient || response.data);
      // Quiet = row autosave on /os: no toast per row, no list reload (the screen shows its own ✓).
      if (options?.quiet) return;
      showToast(t('toast.updated', { item: t('sidebar.osBasicSpecification') }), 'success');
      await fetchIngredientsList(1, true);
    } catch (err: any) {
      if (notifyIfStale(err)) throw err;
      const message = err.response?.data?.message || err.message || 'Failed to update ingredient';
      const messageKey = err.response?.data?.messageKey;
      const translatedMessage = messageKey ? t(messageKey) : message;
      showToast(translatedMessage, 'error');
      throw err;
    }
  };

  // Archive an ingredient
  const archiveIngredient = async (id: string, reason: string, validTo?: string, createNewVersion = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const body = { archivedReason: reason, validTo, createNewVersion, expectedVersion: expectedVersionFor(id) };
      const response = await axios.post(`${baseUrl}/${id}/archive`, body, {
        headers: { 'x-auth-token': token, 'Content-Type': 'application/json' },
      });

      setIngredient(null);
      showToast(t('ingredients.archive.success', 'Ingredient archived'), 'success');
      await fetchIngredientsList(1, true);
      await fetchLatestIngredient();
      return response.data;
    } catch (err: any) {
      if (notifyIfStale(err)) throw err;
      const message = err.response?.data?.message || err.message || 'Failed to archive ingredient';
      showToast(message, 'error');
      throw err;
    }
  };

  // Delete an ingredient
  const deleteIngredient = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      await axios.delete(`${baseUrl}/${id}`, {
        headers: {
          'x-auth-token': token,
        },
      });

      showToast(t('toast.deleted', { item: t('sidebar.osBasicSpecification') }), 'success');

      // Refresh list and auto-load latest ingredient (like DocumentsMaterial pattern)
      await fetchIngredientsList(1, true);
      await fetchLatestIngredient();
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to delete ingredient';
      const messageKey = err.response?.data?.messageKey;
      const translatedMessage = messageKey ? t(messageKey) : message;
      showToast(translatedMessage, 'error');
      throw err;
    }
  };

  // Clear current ingredient — stable identity for the same reason as loadIngredientById.
  const clearIngredient = useCallback(() => {
    setIngredient(null);
  }, [setIngredient]);

  // Auto-load latest ingredient on mount (like DocumentsMaterial pattern)
  useEffect(() => {
    if (initialLatestLoadStartedRef.current) return;
    initialLatestLoadStartedRef.current = true;
    fetchLatestIngredient();
  }, [fetchLatestIngredient]);

  // Reset to page 1 when search changes (must run before the fetch effect).
  // Goes through the wrapped `setPage` so `pageRef.current` is updated
  // synchronously too — see the fetch effect below for why that matters.
  useLayoutEffect(() => {
    setPage(1);
    setIngredients([]);
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch list when search, page, or reload flag changes.
  // BUG 3 fix: skip while `search` hasn't caught up to `debouncedSearch` yet —
  // a fetch for text the debounce is about to supersede is always wasted.
  // Also reads `pageRef.current` rather than the `page` closure variable:
  // when the layout effect above fires in the SAME commit as this effect
  // (debounce just settled while `page` was e.g. 3 from earlier scrolling),
  // passive effects still see the pre-re-render `page` value, so relying on
  // the `page` variable here would fire an extra fetch for the stale page
  // before the next render's correct `fetchIngredientsList(1, true)`.
  // `pageRef.current` is written synchronously by `setPage`, so it already
  // reflects the reset in this same commit — collapsing it back to one fetch.
  useEffect(() => {
    if (search !== debouncedSearch) return;
    const currentPage = pageRef.current;
    const fetchKey = `${debouncedSearch}\u0000${currentPage}\u0000${reloadFlag}\u0000${archiveView ? 'archive' : 'active'}`;
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    if (currentPage === 1) fetchIngredientsList(1, true);
    else fetchIngredientsList(currentPage, false);
  }, [fetchIngredientsList, page, reloadFlag, search, debouncedSearch, archiveView]);

  // Reload function for modal open — resets state and forces fresh fetch
  const reloadIngredientsList = useCallback(() => {
    setPage(1);
    setIngredients([]);
    setReloadFlag(prev => prev + 1);
  }, []);

  return {
    ingredient,
    clearIngredient,
    loadingIngredient,
    ingredients,
    loadingList,
    errorList,
    page,
    totalPages,
    total,
    absoluteTotal,
    setPage,
    search,
    setSearch,
    createIngredient,
    loadIngredientById,
    refreshIngredientIfChanged,
    fetchIngredientByItem,
    updateIngredient,
    deleteIngredient,
    archiveIngredient,
    usersList,
    reloadIngredientsList,
    signIngredient,
    removeIngredientSignature,
  };
};
